import { computed, observable } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import { AutoPayStatus, IInvoiceBlockResponse } from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import type { CXInvoiceColumnHeaderBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-column-header-block.model';
import type { CXInvoiceHeaderBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-header-block.model';
import type { CXInvoiceSummaryBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-summary-block.model';
import type { CXServicesBlock } from '~/components/ClientExperience/OneFlow/models/cx-services-block.model';
import type { CXPaymentScheduleBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-schedule-block.model';
import {
    TInvoiceType,
    TInvoiceTypes,
    ServiceSelectionType
} from '~/types/model.types';
import type { CXServiceItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-item-block.model';
import type { CXRecurringScheduleBlock } from '~/components/ClientExperience/OneFlow/models/cx-recurring-schedule-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';

export class CXInvoiceBlock extends CXContainerableBlock {
    type: 'invoice_container_block';
    @observable invoice_type: TInvoiceType;
    @observable sub_total: number;
    @observable total_tax: number;
    @observable total_discount: number;
    @observable total_price: number;

    constructor(
        block: IInvoiceBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;

        this.invoice_type = block.invoice_type;
    }

    @computed
    get isActionBlockForPrint() {
        return true;
    }

    get displayName() {
        return 'invoice';
    }

    @computed
    get invoiceHeaderBlock() {
        return this.childBlocks.find(
            block => block.type === 'invoice_header_block'
        ) as CXInvoiceHeaderBlock;
    }

    @computed
    get invoiceColumnHeaderBlock() {
        return this.childBlocks.find(
            block => block.type === 'invoice_column_header_block'
        ) as CXInvoiceColumnHeaderBlock;
    }

    @computed
    get invoiceSummaryBlock() {
        return this.childBlocks.find(
            block => block.type === 'invoice_summary_block'
        ) as CXInvoiceSummaryBlock;
    }

    @computed
    get invoicePaymentScheduleBlock() {
        return this.childBlocks.find(
            block => block.type === 'payment_schedule_block'
        ) as CXPaymentScheduleBlock;
    }

    @computed
    get invoiceRecurringScheduleBlock() {
        return this.childBlocks.find(
            block => block.type === 'recurring_schedule_block'
        ) as CXRecurringScheduleBlock;
    }

    @computed
    get chargeBlock() {
        return this.flowRevision.chargeBlock;
    }

    @computed
    get invoiceServicesBlock() {
        return this.childBlocks.find(
            block => block.type === 'services_block'
        ) as CXServicesBlock;
    }

    @computed
    get hasIncompleteServiceSelections() {
        return this.invoiceServiceSelectionBlocks.some(
            block => !block.isSelectionComplete
        );
    }

    @computed
    get hasPaidPayments() {
        return (
            this.invoicePaymentScheduleBlock?.hasPaidPayments ||
            this.invoiceRecurringScheduleBlock?.hasPaidPayments
        );
    }

    @computed
    get isRecurringInvoice() {
        return this.invoice_type === TInvoiceTypes.Recurring;
    }

    @computed
    get isServicesSelectionFinal() {
        return !this.hasIncompleteServiceSelections;
    }

    @computed
    get isPaymentAmountKnown() {
        return (
            this.isServicesSelectionFinal &&
            !this.invoiceSummaryBlock.isPendingClientPaymentSelection
        );
    }

    @computed
    get hasSelection() {
        return this.invoiceServiceSelectionBlocks.some(selectionBlock => {
            return (
                selectionBlock.selection_type !==
                ServiceSelectionType.NoSelection
            );
        });
    }

    @computed
    get hasFixedItems() {
        return !isEmpty(this.invoiceFixedItems);
    }

    @computed
    get allMainServiceItemBlocks() {
        return [
            ...this.clientSelectedServiceItemsBlocks,
            ...this.invoiceFixedItems
        ];
    }

    @computed
    get invoiceFixedItems(): CXServiceItemBlock[] {
        return [
            ...this.autoSelectedServiceItemsBlocks,
            ...this.invoiceServicesBlock.childBlocks
        ];
    }

    @computed
    get autoSelectedServiceItemsBlocks() {
        return this.invoiceServiceSelectionBlocks
            .filter(
                selectionBlock =>
                    !selectionBlock.hasSelection &&
                    selectionBlock.send_info_to_invoice
            )
            .flatMap(selectionBlock => selectionBlock.serviceItems);
    }

    // TODO : refactor this function
    /* This function returns the items to be shown on invoice block */
    @computed
    get clientSelectedServiceItemsBlocks() {
        const allSelectedServices: CXServiceItemBlock[] = [];
        this.invoiceServiceSelectionBlocks
            .filter(selectionBlock => selectionBlock.hasSelection)
            .forEach(serviceSelectionBlock => {
                allSelectedServices.push(
                    ...serviceSelectionBlock.selectedServiceBlocks
                );
            });
        return allSelectedServices.reduce(
            (servicesForInvoice: CXServiceItemBlock[], selectedServiceItem) => {
                servicesForInvoice.push(selectedServiceItem);
                return servicesForInvoice;
            },
            []
        );
    }

    @computed
    get invoiceServiceSelectionBlocks() {
        const {
            flowViewUiStore: { selectedFlow }
        } = this.rootStore;
        const serviceSelectionBlocks =
            selectedFlow.sortedServiceSelectionBlocks;
        return serviceSelectionBlocks.filter(block => {
            return block.invoice_block_id === this.block_id;
        });
    }

    @computed
    get autoPayStatus(): AutoPayStatus {
        return this.chargeBlock?.connectedVariable?.value?.auto_payment?.status;
    }

    @computed
    get isAutoPayRequired() {
        return this.chargeBlock?.allow_autopay;
    }

    @computed
    get showAutoPayBanner() {
        const paymentScheduleBlock = this.isRecurringInvoice
            ? this.invoiceRecurringScheduleBlock
            : this.invoicePaymentScheduleBlock;
        return (
            !!paymentScheduleBlock?.nextPaymentBlock &&
            !!this.autoPayStatus &&
            this.autoPayStatus !== AutoPayStatus.Inactive
        );
    }
}
